import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useLayoutEffect, useRef, useState } from 'react';
import { getShellContentScrollElement } from 'shared/shell_content_scroll_registry';
const indicatorDotCss = "iypg2mc";
const indicatorDotStartCss = "ia6xvl4";
const indicatorDotEndCss = "i1vg4ayh";
const indicatorDotRealtimeCss = "i1cbmbdr";
const IndicatorDot = ({
  isEnd,
  isRealtime
}: {
  isEnd?: boolean;
  isRealtime?: boolean;
}) => <div className={classNames(indicatorDotCss, {
  [indicatorDotStartCss]: !isEnd,
  [indicatorDotEndCss]: isEnd,
  [indicatorDotRealtimeCss]: isRealtime
})} />;
const currentTimeIndicatorIsFirstCss = "cuiqg30";
const currentTimeIndicatorIsPhoneCss = "c1a15jk";
const currentTimeIndicatorIsRealtimeCss = "c2088ul";
const currentTimeIndicatorIsAnchoredHiddenCss = "c1gtr3z3";
const currentTimeIndicatorOverlayAtListTopCss = "c1uzvuog";
const currentTimeIndicatorCss = "c1900p5i";

/** Phone only: fixed clone under sticky filter chrome (same top var as scroll-to-today). */
const phonePinnedIndicatorCss = "p7ldahu";
interface CurrentTimeIndicatorProps {
  isFirst?: boolean;
  isRealtime?: boolean;
  /**
   * Paint on the meeting-list top edge without taking layout height (flush first group).
   */
  overlayAtListTop?: boolean;
  className?: string;
}

/**
 * Displays a visual indicator line marking the current time in a meeting list.
 * On phone, once the in-flow line scrolls above the sticky filter chrome, a fixed
 * clone stays pinned under the header so the now-line follows while scrolling
 * through later meetings. Desktop keeps in-flow only.
 */
export const CurrentTimeIndicator = ({
  isFirst,
  isRealtime,
  overlayAtListTop = false,
  className,
  ...restProps
}: CurrentTimeIndicatorProps) => {
  const isPhone = useDeviceType('isPhone');
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);
  useLayoutEffect(() => {
    if (!isPhone) {
      setIsPinned(false);
      return undefined;
    }
    const anchor = anchorRef.current;
    if (!anchor) return undefined;
    const syncPinned = () => {
      const feedTopRaw = getComputedStyle(document.documentElement).getPropertyValue('--meetings-feed-top').trim();
      const feedTop = Number.parseInt(feedTopRaw, 10);
      const pinY = Number.isFinite(feedTop) ? feedTop : 0;
      const {
        top,
        height
      } = anchor.getBoundingClientRect();
      // Pin when the in-flow marker has moved above the sticky chrome bottom.
      setIsPinned(top + height / 2 < pinY);
    };
    syncPinned();
    const scrollRoot = getShellContentScrollElement();
    scrollRoot?.addEventListener('scroll', syncPinned, {
      passive: true
    });
    window.addEventListener('resize', syncPinned);
    return () => {
      scrollRoot?.removeEventListener('scroll', syncPinned);
      window.removeEventListener('resize', syncPinned);
    };
  }, [isPhone]);
  return <>
      <div {...restProps} ref={anchorRef} data-testid="current-time-indicator" data-overlay-at-list-top={overlayAtListTop ? 'true' : undefined} className={classNames(currentTimeIndicatorCss, {
      [currentTimeIndicatorIsFirstCss]: isFirst && !overlayAtListTop,
      [currentTimeIndicatorIsPhoneCss]: isPhone,
      [currentTimeIndicatorIsRealtimeCss]: isRealtime,
      [currentTimeIndicatorIsAnchoredHiddenCss]: isPhone && isPinned,
      [currentTimeIndicatorOverlayAtListTopCss]: overlayAtListTop
    }, className)}>
        <IndicatorDot isRealtime={isRealtime} />
        {!isRealtime && <IndicatorDot isEnd />}
      </div>
      {isPhone && isPinned && <FixedContainer className={classNames(phonePinnedIndicatorCss, {
      [currentTimeIndicatorIsRealtimeCss]: isRealtime
    })} data-testid="current-time-indicator-pinned" aria-hidden>
          <IndicatorDot isRealtime={isRealtime} />
          {!isRealtime && <IndicatorDot isEnd />}
        </FixedContainer>}
    </>;
};

require("./CurrentTimeIndicator.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./CurrentTimeIndicator.tsx");